/**
 * useSpeechToText — thin wrapper over the browser Web Speech API
 * (`SpeechRecognition` / `webkitSpeechRecognition`) for simple dictation into
 * the chat composer. Client-side only; no backend. Supports any BCP-47 language
 * the browser's recognizer offers (e.g. `ar-SA` for Arabic, `en-US`).
 *
 * `onResult` fires on every recognition event with `(final, interim)`: `final`
 * is any phrase that finalized in this event (append it permanently), `interim`
 * is the current in-progress guess (show it live, replacing the previous one).
 * This gives word-by-word feedback while letting the host keep finalized text
 * stable.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSpeechToTextOptions {
  /** BCP-47 language tag, e.g. 'ar-SA' or 'en-US'. Defaults to the browser locale. */
  lang?: string;
  /** Called each event with the newly finalized phrase and the current interim guess. */
  onResult: (final: string, interim: string) => void;
  onError?: (message: string) => void;
}

interface UseSpeechToTextReturn {
  isSupported: boolean;
  isListening: boolean;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

/** Resolve the constructor across prefixes; undefined when unsupported. */
const getRecognitionCtor = (): any => {
  if (typeof window === 'undefined') return undefined;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
};

export function useSpeechToText({
  lang,
  onResult,
  onError,
}: UseSpeechToTextOptions): UseSpeechToTextReturn {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  // Latest callbacks, read through refs so the recognition instance stays stable.
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  onResultRef.current = onResult;
  onErrorRef.current = onError;

  const isSupported = Boolean(getRecognitionCtor());

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    }
    setIsListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    // Tear down any prior instance before starting a fresh session.
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* noop */
      }
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    if (lang) rec.lang = lang;

    rec.onresult = (event: any) => {
      // `event.resultIndex` points at the first result that changed. Results at
      // or after it are either newly finalized (append permanently) or the
      // current interim guess (shown live, replaced next event).
      let newFinal = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) newFinal += transcript;
        else interim += transcript;
      }
      if (newFinal.trim() || interim.trim()) {
        onResultRef.current(newFinal.trim(), interim.trim());
      }
    };
    rec.onerror = (event: any) => {
      // 'no-speech' / 'aborted' are benign — don't surface them as errors.
      const err = event?.error;
      if (err && err !== 'no-speech' && err !== 'aborted') {
        onErrorRef.current?.(String(err));
      }
      setIsListening(false);
    };
    rec.onend = () => setIsListening(false);

    recognitionRef.current = rec;
    try {
      rec.start();
      setIsListening(true);
    } catch {
      // start() throws if called while already running — ignore.
    }
  }, [lang]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  // Stop recognition if the component unmounts mid-dictation.
  useEffect(() => {
    return () => {
      const rec = recognitionRef.current;
      if (rec) {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
    };
  }, []);

  return { isSupported, isListening, start, stop, toggle };
}

export default useSpeechToText;
